import React, { useState, useRef } from "react";
import {
  Table,
  Tag,
  Space,
  message,
  Popconfirm,
} from "antd";
import { getTablePagination } from "../../../utils/tablePagination";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import EditAppointmentModal from "./EditAppointmentModal";
import tagStyles from "../../../styles/Custom/Tag.module.css";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import { updateAppointmentApi } from "../../../services/receptionist.services";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";
import styles from "./Appointments.module.css";

const AppointmentsTable = ({ data, refresh, page, setPage, loading }) => {
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const tableData = data?.data ?? [];
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    tableData.length
  );

  const onCancel = (id) => {
    updateAppointmentApi({ status: "cancelled", appointment_id: id })
      .then((res) => {
        message.success(res?.data?.message);
        refresh();
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const onMarkReported = (id) => {
    updateAppointmentApi({ status: "reported", appointment_id: id })
      .then((res) => {
        message.success(res?.data?.message || "Marked as reported");
        refresh();
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const columns = [
    {
      title: "Name of Patient",
      dataIndex: "patient_first_name",
      key: "patient_first_name",
      width: isMobile ? 140 : undefined,
      ellipsis: isMobile,
      render: (value, record) =>
        `${value ? capitalizeFirstLetter(value) : ""} ${
          record?.patient_last_name
            ? capitalizeFirstLetter(record.patient_last_name)
            : ""
        }`,
    },
    {
      title: "Date",
      dataIndex: "appointment_date",
      key: "appointment_date",
      width: isMobile ? 120 : undefined,
      render: (value) => dayjs(value).format("DD MMM, YYYY"),
    },
    {
      title: "Slot",
      dataIndex: "appointment_time",
      key: "appointment_time",
      width: isMobile ? 90 : undefined,
      render: (value) =>
        dayjs(value?.split(":").slice(0, 2).join(":"), "HH:mm")?.format(
          "hh:mm A"
        ),
    },
    {
      title: "Practitioner Name",
      dataIndex: "doctor_name",
      key: "doctor_name",
      width: isMobile ? 140 : undefined,
      ellipsis: isMobile,
      render: (value) =>
        value
          ? `${capitalizeFirstLetter(value.split(" ")[0])} ${capitalizeFirstLetter(
              value.split(" ")[1]
            )}`
          : "—",
    },
    {
      title: "Appointment For",
      dataIndex: "appointment_for",
      key: "appointment_for",
      width: isMobile ? 130 : undefined,
      render: (value) =>
        value === "consultation" ? (
          <Tag color="purple">Consultation</Tag>
        ) : (
          <Tag color="blue">Session</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: isMobile ? 110 : undefined,
      render: (value) => (
        <Tag
          color={
            value === "reported"
              ? "green"
              : value === "scheduled"
              ? "orange"
              : "magenta"
          }
          className={tagStyles.mdRndTag}
        >
          {capitalizeFirstLetter(value)}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: isMobile ? 100 : undefined,
      fixed: isMobile ? "right" : undefined,
      render: (_, record) => (
        <Space>
          {record?.status !== "cancelled" && record?.status !== "reported" && (
            <>
              <Popconfirm
                title="Mark this appointment as reported?"
                okText="Yes"
                cancelText="No"
                icon={null}
                onConfirm={() => onMarkReported(record?.id)}
              >
                <ActionIconButton aria-label="Mark as reported">
                  <CheckOutlined style={{ color: "#12b76a" }} />
                </ActionIconButton>
              </Popconfirm>
              {!dayjs(record?.appointment_date).isBefore(dayjs(), "day") && (
                <Popconfirm
                  title="Sure to cancel appointment?"
                  okText="Yes"
                  cancelText="No"
                  icon={null}
                  onConfirm={() => onCancel(record?.id)}
                >
                  <ActionIconButton aria-label="Cancel appointment" danger>
                    <CloseOutlined style={{ color: "red" }} />
                  </ActionIconButton>
                </Popconfirm>
              )}
              <ActionIconButton
                aria-label="Edit appointment"
                onClick={() => {
                  setOpen(true);
                  setEditData(record);
                }}
              >
                <EditIcon />
              </ActionIconButton>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div
      ref={tableWrapRef}
      className={`${styles.receptionAppointmentsTable} ui-table-fill-body`}
    >
      <Table
        className="no-vertical-borders"
        loading={loading}
        dataSource={tableData}
        columns={columns}
        rowKey="id"
        pagination={getTablePagination({
          current: page,
          pageSize: 50,
          total: data?.total_count ?? 0,
          onChange: (p) => setPage(p),
        })}
        scroll={tableScroll}
      />

      {open && (
        <EditAppointmentModal
          open={open}
          setOpen={setOpen}
          editData={editData}
          refresh={refresh}
        />
      )}
    </div>
  );
};

export default AppointmentsTable;
